import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams } from 'react-router-dom';
import { apiUrl } from '../config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VerifyResponse {
  valid: boolean;
  workOrderTitle?: string;
  status?: string;
  signedAt?: string;
  technicianName?: string;
  technicianSignedAt?: string;
  clientName?: string;
  clientSignedAt?: string;
  documentHash?: string;
  informeFileName?: string;
  checklistFileName?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago'
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusLabel(status?: string): string {
  const map: Record<string, string> = {
    FULLY_SIGNED: 'Completamente Firmado',
    TECH_SIGNED: 'Firmado por Técnico',
    CLIENT_SIGNED: 'Firmado por Cliente',
    APPROVED: 'Aprobado',
    DRAFT: 'Borrador'
  };
  return status ? (map[status] ?? status) : '—';
}

function statusColor(
  status?: string
): 'success' | 'warning' | 'info' | 'default' {
  if (status === 'FULLY_SIGNED') return 'success';
  if (status === 'TECH_SIGNED' || status === 'CLIENT_SIGNED') return 'warning';
  if (status === 'APPROVED') return 'info';
  return 'default';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopBar() {
  return (
    <Box
      sx={{
        bgcolor: '#1a1a2e',
        color: '#fff',
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}
    >
      <Box
        component="img"
        src="/ferrari-logo.png"
        alt="Ferrari Crane"
        sx={{ height: 32, width: 'auto', flexShrink: 0 }}
      />
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, letterSpacing: '0.04em', color: '#fff' }}
      >
        Ferrari Crane
      </Typography>
      <Typography
        variant="caption"
        sx={{
          ml: 'auto',
          color: 'rgba(255,255,255,0.55)',
          display: { xs: 'none', sm: 'block' }
        }}
      >
        Verificación de documento
      </Typography>
    </Box>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 0.25, sm: 1 },
        py: 0.75
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 180, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function VerificationPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<VerifyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) {
      setData({ valid: false });
      setLoading(false);
      return;
    }

    fetch(`${apiUrl}signatures/public/verify/${id}`, {
      headers: { Accept: 'application/json' }
    })
      .then((res) => res.json())
      .then((body: VerifyResponse) => {
        setData(body);
        setLoading(false);
      })
      .catch(() => {
        setData({ valid: false });
        setLoading(false);
      });
  }, [id]);

  const handleCopyHash = () => {
    if (!data?.documentHash) return;
    navigator.clipboard.writeText(data.documentHash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const truncateHash = (hash?: string) => {
    if (!hash) return '—';
    return hash.length > 20 ? `${hash.slice(0, 10)}...${hash.slice(-10)}` : hash;
  };

  if (loading) {
    return (
      <>
        <TopBar />
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <TopBar />

      <Container
        maxWidth="sm"
        sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          >
            Verificación de documento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compruebe la autenticidad de un informe técnico firmado
          </Typography>
        </Box>

        {/* Status card */}
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            {data?.valid ? (
              <>
                <CheckCircleTwoToneIcon
                  sx={{ fontSize: 64, color: 'success.main', mb: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Documento Verificado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Este documento es auténtico y fue firmado digitalmente.
                </Typography>
              </>
            ) : (
              <>
                <CancelTwoToneIcon
                  sx={{ fontSize: 64, color: 'error.main', mb: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Documento No Encontrado
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 340, mx: 'auto' }}
                >
                  No se encontró ningún documento con este identificador. Es
                  posible que el QR sea inválido o el documento no haya sido
                  generado correctamente.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Details card — only shown when valid */}
        {data?.valid && (
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              {/* Work order + status */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Orden de trabajo
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {data.workOrderTitle ?? '—'}
                  </Typography>
                </Box>
                <Chip
                  label={statusLabel(data.status)}
                  color={statusColor(data.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* Dates */}
              <InfoRow
                label="Fecha de firma completa"
                value={formatDate(data.signedAt)}
              />

              <Divider sx={{ my: 1 }} />

              {/* Technician */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
              >
                Técnico
              </Typography>
              <InfoRow label="Nombre" value={data.technicianName ?? '—'} />
              <InfoRow
                label="Firmado el"
                value={formatDate(data.technicianSignedAt)}
              />

              <Divider sx={{ my: 1 }} />

              {/* Client */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
              >
                Cliente
              </Typography>
              <InfoRow label="Nombre" value={data.clientName ?? '—'} />
              <InfoRow
                label="Firmado el"
                value={formatDate(data.clientSignedAt)}
              />

              <Divider sx={{ my: 1 }} />

              {/* Documents */}
              {(data.informeFileName || data.checklistFileName) && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                  >
                    Documentos
                  </Typography>
                  {data.informeFileName && (
                    <InfoRow
                      label="Informe técnico"
                      value={data.informeFileName}
                    />
                  )}
                  {data.checklistFileName && (
                    <InfoRow label="Checklist" value={data.checklistFileName} />
                  )}
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* Document hash */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
              >
                Hash del documento
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    flexGrow: 1,
                    wordBreak: 'break-all'
                  }}
                >
                  {data.documentHash
                    ? truncateHash(data.documentHash)
                    : 'No disponible'}
                </Typography>
                {data.documentHash && (
                  <Tooltip title={copied ? 'Copiado' : 'Copiar hash completo'}>
                    <IconButton size="small" onClick={handleCopyHash}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          align="center"
          sx={{ display: 'block', mt: 4 }}
        >
          Ferrari Crane — Portal de verificación de documentos firmados
        </Typography>
      </Container>
    </>
  );
}
